<template>
  <v-card flat>
    <v-card-text>
      <!-- Summary Cards -->
      <v-row v-if="report" class="mb-4">
        <v-col cols="6" sm="3">
          <v-card color="blue darken-1" dark class="pa-3 text-center" flat>
            <div class="text-h5 font-weight-bold">
              {{ report.total_service_types }}
            </div>
            <div class="caption">Service Types</div>
          </v-card>
        </v-col>
        <v-col cols="6" sm="3">
          <v-card color="green darken-1" dark class="pa-3 text-center" flat>
            <div class="text-h5 font-weight-bold">
              {{ formatCurrency(report.grand_total_revenue) }}
            </div>
            <div class="caption">Total Revenue</div>
          </v-card>
        </v-col>
        <v-col cols="6" sm="3">
          <v-card color="purple darken-1" dark class="pa-3 text-center" flat>
            <div class="text-h5 font-weight-bold">
              {{ report.grand_total_times_rendered }}
            </div>
            <div class="caption">Times Rendered</div>
          </v-card>
        </v-col>
        <v-col cols="6" sm="3">
          <v-card color="teal darken-1" dark class="pa-3 text-center" flat>
            <div class="text-h6 font-weight-bold">Period</div>
            <div class="caption">
              {{ report.from_date }} - {{ report.to_date }}
            </div>
          </v-card>
        </v-col>
      </v-row>

      <!-- Data Table -->
      <v-data-table
        v-if="report"
        :items="report.items"
        :headers="headers"
        :search="search"
        dense
        :items-per-page="25"
        :footer-props="{ 'items-per-page-options': [10, 25, 50, 100, -1] }"
      >
        <template v-slot:top>
          <v-text-field
            v-model="search"
            prepend-icon="mdi-magnify"
            label="Search services..."
            dense
            class="mb-2"
          />
        </template>
        <template v-slot:item.base_price="{ item }">
          {{ formatCurrency(item.base_price) }}
        </template>
        <template v-slot:item.total_revenue="{ item }">
          <span class="font-weight-bold green--text text--darken-2">
            {{ formatCurrency(item.total_revenue) }}
          </span>
        </template>
        <template v-slot:item.avg_revenue_per_invoice="{ item }">
          {{ formatCurrency(item.avg_revenue_per_invoice) }}
        </template>
        <template v-slot:item.profit_margin="{ item }">
          <v-chip
            :color="
              item.profit_margin >= 50
                ? 'green'
                : item.profit_margin >= 20
                ? 'orange'
                : 'red'
            "
            dark
            small
          >
            {{ item.profit_margin }}%
          </v-chip>
        </template>
      </v-data-table>

      <!-- Empty State -->
      <div v-if="!report" class="text-center grey--text pa-10">
        <v-icon size="64" color="grey lighten-1">mdi-currency-php</v-icon>
        <p class="mt-4 subtitle-1">
          Click "Generate" to see service revenue breakdown.
        </p>
        <p class="caption grey--text">
          Uses the date range from the filters above. Default: last 30 days.
        </p>
      </div>

      <!-- No Results -->
      <div
        v-if="report && report.items && report.items.length === 0"
        class="text-center pa-10"
      >
        <v-icon size="64" color="grey">mdi-cart-off</v-icon>
        <p class="mt-4 subtitle-1 grey--text">
          No service revenue data found for the selected period.
        </p>
      </div>
    </v-card-text>
  </v-card>
</template>

<script>
import { mapActions } from "vuex";
export default {
  name: "ServiceRevenueSummary",
  props: {
    filters: { type: Object, required: true },
  },
  data() {
    return {
      report: null,
      search: "",
      headers: [
        { text: "#", value: "rank", width: "50px", sortable: false },
        { text: "Service Name", value: "service_name" },
        { text: "Base Price", value: "base_price", align: "end" },
        { text: "Times Rendered", value: "times_rendered", align: "center" },
        { text: "Total Qty", value: "total_qty", align: "center" },
        { text: "Total Revenue", value: "total_revenue", align: "end" },
        {
          text: "Avg Revenue/Invoice",
          value: "avg_revenue_per_invoice",
          align: "end",
        },
        { text: "Profit Margin", value: "profit_margin", align: "center" },
      ],
    };
  },
  methods: {
    ...mapActions({
      fetchReport: "service_reports/get_revenue_summary_report",
    }),
    formatCurrency(value) {
      if (!value && value !== 0) return "0.00";
      return Number(value).toLocaleString("en-PH", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },
    async generate() {
      try {
        let params = {};
        if (this.filters.from_date) params.from_date = this.filters.from_date;
        if (this.filters.to_date) params.to_date = this.filters.to_date;
        if (this.filters.branch_id) params.branch_id = this.filters.branch_id;
        let data = await this.fetchReport(params);
        if (data && data.items) {
          data.items = data.items.map((item, idx) => ({
            ...item,
            rank: idx + 1,
          }));
        }
        this.report = data;
      } catch (e) {
        this.$alert({
          title: "Error",
          message:
            "Error generating revenue summary: " +
            (e.response?.data?.error || e.message),
          color: "error",
          icon: "mdi-alert-circle",
        });
      }
    },
  },
};
</script>
